import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import type { MfaMethodType } from '@eye-able/types';
import { ChevronRight, Mail, Smartphone } from 'lucide-react';
import MfaPill from './MfaPill';

/** Works offline and cannot be read along like a mailbox. */
export const RECOMMENDED_METHOD: MfaMethodType = 'totp';

interface MethodChoiceProps {
  /** The methods the user may still set up. */
  options: MfaMethodType[];
  onSelect: (type: MfaMethodType) => void;
  disabled?: boolean;
}

const icons = {
  totp: Smartphone,
  email: Mail,
} as const;

/** Picks which second factor to set up. */
function MethodChoice({
  options,
  onSelect,
  disabled = false,
}: MethodChoiceProps) {
  const { t } = useInlineTranslation();

  return (
    <ul className='eye:flex eye:flex-col eye:gap-2xs'>
      {options.map((type) => {
        const Icon = icons[type];
        return (
          <li key={type}>
            <button
              type='button'
              disabled={disabled}
              onClick={() => onSelect(type)}
              className='eye:flex eye:w-full eye:cursor-pointer eye:items-center eye:gap-16 eye:rounded-md eye:border eye:border-grey-100 eye:bg-white eye:p-xs eye:text-left eye:transition-colors eye:hover:bg-grey-5 eye:focus-visible:outline-2 eye:focus-visible:outline-offset-2 eye:disabled:cursor-not-allowed eye:disabled:opacity-60'
            >
              <Icon aria-hidden className='eye:shrink-0' />
              <span className='eye:flex eye:flex-1 eye:flex-col'>
                <span className='eye:flex eye:flex-wrap eye:items-center eye:gap-8 eye:font-semibold'>
                  {t(`mfaMethod_${type}`)}
                  {type === RECOMMENDED_METHOD ? (
                    <MfaPill tone='recommended'>{t('mfaRecommended')}</MfaPill>
                  ) : null}
                </span>
                <span className='eye:text-12 eye:leading-[18px] eye:text-grey-120'>
                  {t(`mfaMethodHint_${type}`)}
                </span>
              </span>
              <ChevronRight aria-hidden className='eye:shrink-0' />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default MethodChoice;
